import { get } from 'lodash';
import { resolve } from 'redux-optimist-promise';
import {
  FETCH_PROPOSALS,
  FETCH_PROPOSALS_BY_ID,
  UPDATE_ATTENDEES,
} from 'Store/booking/proposals/constants';
import { FETCH_PREBOOKING_TRAVELERS } from 'Store/prebooking-form/prebookingform.action';
import { SIGNOUT } from 'Store/user-info/actions/signout';
import { SET_ATTENDEES } from 'Store/trip-information/adults/constants';
import { apiDateToTimestamp } from 'Utils/date/apiDate';
import {
  DEFAULT_KID_VALUE,
  TRIP_INFORMATION_UPDATE_KID,
  TRIP_INFORMATION_SET_KIDS,
  TRIP_INFORMATION_RESET_KIDS,
  TRIP_INFORMATION_SET_KIDS_WITHOUT_CLEAR_PROPOSALS,
} from './constants';
export type Kids = {
  readonly birthdate: timestamp | null | undefined;
  readonly id?: string;
  readonly neolid: string | null | undefined;
  readonly childcareIncluded: boolean;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly mainContactId?: string;
};
export type State = Array<Kids>;
const initialState: State = [];

function updateKid(state: State, action: Action<any>): State {
  const { index, valuesToUpdate } = get(action, 'payload');

  if (!Number.isFinite(index)) {
    return state;
  }

  const kidsState = state || [];
  return Array(index >= kidsState.length ? index + 1 : kidsState.length)
    .fill({ ...DEFAULT_KID_VALUE })
    .map((defaultKid, i) => {
      if (i !== index) {
        return kidsState[i] ? kidsState[i] : { ...DEFAULT_KID_VALUE };
      }

      return { ...{ ...(kidsState[i] ? kidsState[i] : defaultKid) }, ...valuesToUpdate };
    });
}

const updateKids = (state: State, { payload }: Action<any>) => {
  if (!payload.kidsAttendees) {
    return state.map((kid) => ({
      birthdate: kid.birthdate,
      neolid: kid.neolid,
      childcareIncluded: kid.childcareIncluded,
      mainContactId: undefined,
    }));
  }

  return state.map((kid, index) => {
    const { birthdate, ...restAttendeeData } = get(payload, `kidsAttendees[${index}]`, {});
    return {
      ...restAttendeeData,
      birthdate: kid.birthdate,
      neolid: kid.neolid,
      childcareIncluded: kid.childcareIncluded,
      mainContactId: undefined,
    };
  });
};

export function kidsReducer(state: State = initialState, action: Action<any>): State {
  switch (action.type) {
    case TRIP_INFORMATION_UPDATE_KID:
      return updateKid(state, action);

    case TRIP_INFORMATION_SET_KIDS: {
      const kids: State = get(action, 'payload.kids') || [];
      return kids.map((kid) => kid || { ...DEFAULT_KID_VALUE });
    }

    case TRIP_INFORMATION_SET_KIDS_WITHOUT_CLEAR_PROPOSALS: {
      const kids: State = get(action, 'payload.kids') || [];
      return kids.map((kid, index) =>
        kid
          ? {
              ...state[index],
              ...kid,
              birthdate:
                typeof kid.birthdate === 'string'
                  ? apiDateToTimestamp(kid.birthdate)
                  : kid.birthdate,
              neolid: kid.neolid || get(state[index], 'neolid'),
              mainContactId: kid.neolid ? undefined : kid.mainContactId,
            }
          : { ...DEFAULT_KID_VALUE },
      );
    }

    case resolve(FETCH_PROPOSALS):
    case resolve(FETCH_PROPOSALS_BY_ID):
    case resolve(FETCH_PREBOOKING_TRAVELERS):
    case SET_ATTENDEES:
      return updateKids(state, action);

    case UPDATE_ATTENDEES: {
      if (!action.payload.attendees) {
        return state;
      }

      return state.map((kid) => {
        const updatedKid = action.payload.attendees.find(({ id }) => id === kid.id) || {};
        return { ...kid, neolid: updatedKid.customerId, gmNumber: updatedKid.gmNumber };
      });
    }

    case TRIP_INFORMATION_RESET_KIDS:
      return initialState;

    case SIGNOUT:
      return initialState;

    default:
      return state;
  }
}
